/**
 * The cost section of the Cloud board (L1): headline tiles, the top-10
 * subscriptions as a sorted horizontal bar (compare magnitudes → bar,
 * dashboard design rules §2), and the 30-day trend line with Azure's forecast
 * as a reference line. The per-subscription figures stay reachable as each
 * chart's table twin (§4.4).
 *
 * Two honesty rules shape this section. First, the trend points are *month-to-
 * date as collected each day*, not daily spend: the line climbs through a month
 * and resets at the month boundary, and it is labelled that way. Second, the
 * trend is estate-wide — the backend keeps no per-subscription series, so a
 * subscription filter narrows the bar and the tiles but never fakes a filtered
 * trend.
 */

import { Chart } from "@norican/dataviz";
import { EmptyState, Statistic, Typography } from "@norican/react";

import { type BoardRange, ChartCard } from "@/components/board";
import type { CloudCost, SubscriptionCost } from "@/lib/api";
import {
  formatCount,
  formatCurrency,
  formatCurrencyCompact,
  identityConfig,
  subscriptionConfig,
} from "@/lib/charts/config";
import { useDrillParams } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { SUB_PARAM } from "./params";
import { formatDay } from "./status";

/** How many subscriptions the ranking bar shows — the rest are in View data. */
const TOP_LIMIT = 10;

const TREND_CONFIG = identityConfig(["actual_cost"], { actual_cost: "Month-to-date cost" });

interface CostSectionProps {
  cost: CloudCost;
  /** Selected subscription IDs from the board FilterRow ([] = all). */
  subIds: string[];
  /** The board's date window — slices the trend (30 days of history exist). */
  range: BoardRange;
}

export function CostSection({ cost, subIds, range }: CostSectionProps) {
  const drill = useDrillParams(SUB_PARAM);

  if (!cost.configured) {
    return (
      <section id="cloud-cost" className="scroll-mt-6">
        <PanelCard title="Cost" caption="Not available">
          <Typography variant="body-sm" tone="muted">
            {cost.status_detail}
          </Typography>
        </PanelCard>
      </section>
    );
  }

  if (cost.latest_day == null) {
    return (
      <section id="cloud-cost" className="scroll-mt-6">
        <PanelCard title="Cost" caption="No data yet">
          <EmptyState
            size="sm"
            title="No cost data collected yet"
            description={cost.status_detail}
          />
        </PanelCard>
      </section>
    );
  }

  const scoped = subIds.length > 0;
  const subscriptions = scoped
    ? cost.subscriptions.filter((subscription) => subIds.includes(subscription.subscription_id))
    : cost.subscriptions;

  // Month-to-date is by definition the sum of each subscription's latest
  // figure, so a subscription-filtered sum stays an honest number.
  //
  // A subscription whose cost could not be read (Cost Management denied by the
  // enrolment, or simply not collected yet) carries null, not 0 — summing it as
  // zero would understate the estate and read as "this subscription is free".
  // Those subscriptions are excluded from the sum and counted, so the tile can
  // say what it is missing (§5, no data ≠ zero).
  const sumCost = (pick: (row: SubscriptionCost) => number | null | undefined): number =>
    subscriptions.reduce((sum, subscription) => sum + (pick(subscription) ?? 0), 0);
  const missingCost = subscriptions.filter(
    (subscription) => subscription.actual_cost == null,
  ).length;
  const coverageNote =
    missingCost > 0 ? ` · ${formatCount(missingCost)} without a cost figure, excluded` : "";

  const monthToDate = scoped ? sumCost((row) => row.actual_cost) : cost.month_to_date;
  const amortized = scoped ? sumCost((row) => row.amortized_cost) : cost.amortized_month_to_date;

  // Colours are assigned from the FULL subscription list, so filtering never
  // repaints the survivors (§3.3).
  const barConfig = subscriptionConfig(cost.subscriptions);
  const ranked = [...subscriptions].sort((a, b) => (b.actual_cost ?? 0) - (a.actual_cost ?? 0));
  const barRows = ranked.slice(0, TOP_LIMIT);

  const trendRows = cost.trend
    .filter((point) => point.day >= range.fromIso && point.day <= range.toIso)
    .map((point) => ({ ...point, label: formatDay(point.day) }));

  return (
    <section id="cloud-cost" className="flex scroll-mt-6 flex-col gap-4">
      <PanelCard
        title="Cost"
        caption={`Month to date · latest collection ${formatDay(cost.latest_day)}`}
      >
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Statistic
            label="Month to date"
            value={formatCurrency(monthToDate, cost.currency)}
            tone="accent"
            size="lg"
            caption={
              scoped
                ? `Actual cost across the ${subscriptions.length} selected subscriptions${coverageNote}`
                : `Actual cost across ${cost.subscriptions.length} subscriptions${coverageNote}`
            }
          />
          <Statistic
            label="Forecast (month)"
            value={
              cost.forecast
                ? formatCurrency(cost.forecast.month_total, cost.forecast.currency)
                : "—"
            }
            caption={
              cost.forecast
                ? `Azure's tenant-wide projection, collected ${formatDay(cost.forecast.as_of_day)}${
                    scoped ? " — unaffected by the subscription filter" : ""
                  }`
                : "Not collected yet"
            }
          />
          <Statistic
            label="Amortized MTD"
            value={formatCurrency(amortized, cost.currency)}
            caption="Reservations spread over their term"
          />
          <Statistic
            label="Collected today"
            value={`${formatCount(cost.subscriptions_collected_today)} / ${formatCount(
              cost.subscriptions.length,
            )}`}
            caption="Subscriptions with a cost row written today"
          />
        </div>
        {cost.errors.length > 0 && (
          <Typography variant="body-sm" tone="muted">
            Collection problems: {cost.errors.join(" · ")}
          </Typography>
        )}
      </PanelCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title="Cost by subscription"
          caption={
            subscriptions.length > TOP_LIMIT
              ? `Top ${TOP_LIMIT} of ${subscriptions.length} — click a bar to focus the board`
              : "Month to date, most expensive first — click a bar to focus the board"
          }
          tableTwin={{
            id: "cost-by-subscription",
            ariaLabel: "Month-to-date cost per subscription",
            data: ranked,
            getRowId: (row: SubscriptionCost) => row.subscription_id,
            columns: [
              { id: "name", header: "Subscription", accessor: (row) => row.name },
              {
                id: "actual",
                header: "Month to date",
                accessor: (row) => row.actual_cost,
                align: "end",
                cell: (row) => (
                  <span className="tabular-nums">
                    {formatCurrency(row.actual_cost, row.currency)}
                  </span>
                ),
              },
              {
                id: "amortized",
                header: "Amortized",
                accessor: (row) => row.amortized_cost,
                align: "end",
                cell: (row) => (
                  <span className="tabular-nums">
                    {formatCurrency(row.amortized_cost, row.currency)}
                  </span>
                ),
              },
              {
                id: "as-of",
                header: "As of",
                accessor: (row) => row.as_of_day,
                cell: (row) => <span className="tabular-nums">{formatDay(row.as_of_day)}</span>,
              },
            ],
          }}
        >
          <Chart.Container
            config={barConfig}
            className="h-72"
            aria-label={`Month-to-date cost for the top ${barRows.length} subscriptions`}
          >
            <Chart.BarChart
              data={barRows}
              layout="vertical"
              margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
            >
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              <Chart.XAxis
                {...Chart.axisProps}
                type="number"
                tickFormatter={(value: number) => formatCurrencyCompact(value, cost.currency)}
              />
              <Chart.YAxis {...Chart.axisProps} type="category" dataKey="name" width={140} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={
                  <Chart.TooltipContent
                    formatter={(value) => formatCurrency(Number(value), cost.currency)}
                    hideLabel
                  />
                }
              />
              <Chart.Bar
                {...Chart.seriesDefaults}
                data={barRows}
                dataKey="actual_cost"
                colorKey="name"
                radius={[0, 3, 3, 0]}
                cursor="pointer"
                onClick={(_, index) => {
                  const row = barRows[index];
                  if (row) {
                    drill.set({ [SUB_PARAM]: [row.subscription_id] });
                  }
                }}
              />
            </Chart.BarChart>
          </Chart.Container>
        </ChartCard>

        <ChartCard
          title="Cost trend"
          caption={`${trendRows.length} collection days${scoped ? " · estate-wide (no per-subscription history)" : ""}`}
          tableTwin={{
            id: "cost-trend",
            ariaLabel: "Estate month-to-date cost per collection day",
            data: trendRows,
            getRowId: (row) => row.day,
            columns: [
              { id: "day", header: "Day", accessor: (row) => row.label },
              {
                id: "actual",
                header: "Month to date",
                accessor: (row) => row.actual_cost,
                align: "end",
                cell: (row) => (
                  <span className="tabular-nums">
                    {formatCurrency(row.actual_cost, cost.currency)}
                  </span>
                ),
              },
              {
                id: "subs",
                header: "Subscriptions",
                accessor: (row) => row.subscriptions,
                align: "end",
                cell: (row) => (
                  <span className="tabular-nums">{formatCount(row.subscriptions)}</span>
                ),
              },
            ],
          }}
        >
          {trendRows.length < 2 ? (
            <EmptyState
              size="sm"
              title="Not enough history for a trend yet"
              description="The daily cost sweep has to run for at least two days in this window."
            />
          ) : (
            <div className="flex flex-col gap-2">
              <Chart.Container
                config={TREND_CONFIG}
                className="h-64"
                aria-label={`Estate month-to-date cost as collected, last ${trendRows.length} days`}
              >
                <Chart.LineChart
                  data={trendRows}
                  margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
                >
                  <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                  <Chart.XAxis {...Chart.axisProps} dataKey="label" />
                  <Chart.YAxis
                    {...Chart.axisProps}
                    width={56}
                    tickFormatter={(value: number) => formatCurrencyCompact(value, cost.currency)}
                  />
                  <Chart.Tooltip
                    content={
                      <Chart.TooltipContent
                        formatter={(value) => formatCurrency(Number(value), cost.currency)}
                      />
                    }
                  />
                  {cost.forecast && (
                    <Chart.ReferenceLine
                      y={cost.forecast.month_total}
                      stroke="var(--norican-fg-muted)"
                      strokeDasharray="4 4"
                      label={{
                        value: `Forecast ${formatCurrencyCompact(
                          cost.forecast.month_total,
                          cost.forecast.currency,
                        )}`,
                        position: "insideTopRight",
                        fontSize: 11,
                        fill: "var(--norican-fg-muted)",
                      }}
                    />
                  )}
                  <Chart.Line
                    {...Chart.seriesDefaults}
                    dataKey="actual_cost"
                    type="monotone"
                    stroke="var(--color-actual_cost)"
                    strokeWidth={2}
                    dot={false}
                  />
                </Chart.LineChart>
              </Chart.Container>
              <Typography variant="body-sm" tone="muted">
                Month-to-date as collected each day — the line climbs through a month and resets at
                the month boundary. It is not daily spend.
              </Typography>
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
}
